// Portraits of the towers and the enemy for the HUD, rendered from their own 3D models: each
// model is loaded once, lit and framed from the game's own angle in a small offscreen renderer,
// and kept as an image. Until a portrait is ready (or where there is no WebGL) a line silhouette stands in.
import * as THREE from 'three';
import { silhouette } from './icons.ts';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { stripSlab } from './bases3d.ts';

const SIZE = 112;
/** Systems without a model of their own borrow the nearest look, as the scene does. */
const STAND_IN: Record<string, string> = {
  patriot: 'missile_defence', thaad: 'missile_defence', bastion: 'missile_battery', atgm_post: 'sniper_nest', at_mine: 'land_mine',
  strike_jet: 'jet', airbase: 'airfield', conscript: 'conscript', drone_ship: 'landing_craft', hgv_nuclear: 'hgv',
};
/** Things that read better framed a little closer or further. */
const ZOOM: Record<string, number> = { land_mine: 1.6, at_mine: 1.6, fpv_drone: 1.3, scout_drone: 1.2, hq: 0.95, airbase: 0.95 };

const done = new Map<string, string | null>();
const pending = new Set<string>();
const loader = new GLTFLoader();
let stage: { renderer: THREE.WebGLRenderer; scene: THREE.Scene; camera: THREE.OrthographicCamera; holder: THREE.Group } | null | undefined;

function setUp(): typeof stage {
  if (stage !== undefined) return stage;
  try {
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setSize(SIZE, SIZE, false);
    renderer.setPixelRatio(1);
    renderer.setClearColor(0x000000, 0);
    renderer.toneMapping = THREE.NeutralToneMapping;
    const scene = new THREE.Scene();
    scene.add(new THREE.HemisphereLight(0xdfe8ff, 0x3a3323, 1.4));
    const sun = new THREE.DirectionalLight(0xfff0d8, 2.6);
    sun.position.set(-3, 6, 2);
    scene.add(sun);
    const back = new THREE.DirectionalLight(0x9ab8ff, 0.8);
    back.position.set(3, 2, -4);
    scene.add(back);
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 100);
    const holder = new THREE.Group();
    scene.add(holder);
    stage = { renderer, scene, camera, holder };
  } catch { stage = null; }
  return stage;
}

/** Frames and renders one model, and returns the image. */
function shoot(root: THREE.Object3D, type: string): string | null {
  const st = setUp();
  if (!st) return null;
  const { renderer, scene, camera, holder } = st;
  holder.clear();
  holder.add(root);
  root.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(root);
  const size = box.getSize(new THREE.Vector3());
  const centre = box.getCenter(new THREE.Vector3());
  // the game's view: 30 degrees up, 45 round, a touch more from above so a flat thing shows
  const r = Math.max(size.x, size.y, size.z) * 0.62 / (ZOOM[type] ?? 1);
  camera.left = -r; camera.right = r; camera.top = r; camera.bottom = -r;
  camera.position.copy(centre).add(new THREE.Vector3(10, 8, 10));
  camera.lookAt(centre);
  camera.updateProjectionMatrix();
  renderer.render(scene, camera);
  const url = renderer.domElement.toDataURL('image/png');
  holder.clear();
  return url;
}

/** Swaps the silhouette for the image in every element waiting on this type. */
function deliver(type: string, url: string): void {
  for (const el of document.querySelectorAll<HTMLElement>(`[data-portrait="${type}"]`)) {
    const img = document.createElement('img');
    img.src = url;
    img.alt = '';
    img.draggable = false;
    el.replaceChildren(img);
    el.classList.add('has-portrait');
  }
}

/** Starts loading a portrait if it is not there; the elements waiting on it are filled when it lands. */
export function requestPortrait(type: string): void {
  if (done.has(type) || pending.has(type)) return;
  if (!setUp()) { done.set(type, null); return; }
  pending.add(type);
  const name = STAND_IN[type] ?? type;
  loader.load(`${import.meta.env.BASE_URL}models/${name}.glb`, gltf => {
    const root = gltf.scene;
    stripSlab(name, root);
    const url = shoot(root, type);
    done.set(type, url);
    pending.delete(type);
    if (url) deliver(type, url);
  }, undefined, () => { done.set(type, null); pending.delete(type); });
}

/** The portrait as an image if it is ready, else a line silhouette, in a span the image replaces when it lands. */
export function portraitEl(type: string, cls = 'ico'): HTMLSpanElement {
  const span = document.createElement('span');
  span.className = cls;
  span.dataset.portrait = type;
  span.setAttribute('aria-hidden', 'true');
  const url = done.get(type);
  if (url) {
    const img = document.createElement('img');
    img.src = url;
    img.alt = '';
    img.draggable = false;
    span.appendChild(img);
    span.classList.add('has-portrait');
  } else {
    span.appendChild(silhouette(type));
    if (url === undefined) requestPortrait(type);
  }
  return span;
}
